import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

/**
 * Image upload to object storage.
 * onChange(url): called with the public URL once uploaded.
 */
export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
  const { t } = useLang();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errMsg, setErrMsg] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const fullUrl = value && value.startsWith("/api/") ? `${BACKEND}${value}` : value;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrMsg("");
    // Client-side guards
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(file.type)) {
      const msg = `Type "${file.type || "inconnu"}" non supporté. Utilisez JPG, PNG, WebP ou GIF.`;
      setErrMsg(msg); toast.error(msg);
      if (ref.current) ref.current.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      const msg = `Fichier trop lourd (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 15 MB.`;
      setErrMsg(msg); toast.error(msg);
      if (ref.current) ref.current.value = "";
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // IMPORTANT: ne PAS forcer Content-Type — axios doit auto-générer le boundary.
      const { data } = await api.post("/upload", fd, {
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 60000,
      });
      onChange(data.url);
      toast.success("Image uploadée");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message || "Upload échoué";
      setErrMsg(detail);
      toast.error(detail);
      console.error("[ImageUpload] failure:", err);
    } finally {
      setBusy(false);
      setProgress(0);
      if (ref.current) ref.current.value = "";
    }
  };

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
      <input ref={ref} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={onPick} className="hidden" data-testid={`${testId}-input`} />
      {fullUrl ? (
        <div className="mt-1 relative">
          <img src={fullUrl} alt="" className="aspect-[16/9] w-full rounded-xl object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 rounded-full bg-white/90 p-1.5 text-neutral-700 hover:bg-white" data-testid={`${testId}-clear`}>
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            disabled={busy}
            className="absolute bottom-2 right-2 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-neutral-800 hover:bg-white disabled:opacity-50"
            data-testid={`${testId}-replace`}
          >
            {busy ? t("image_upload_loading") : t("image_upload_replace")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          className="mt-1 flex h-32 w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-wine-200 bg-wine-50/20 text-sm font-semibold text-wine-700 hover:bg-wine-50 disabled:opacity-50"
          data-testid={`${testId}-button`}
        >
          {busy ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> {t("image_upload_loading")} {progress > 0 ? `${progress}%` : ""}</span>
          ) : (
            <span className="inline-flex items-center gap-2"><Upload className="h-4 w-4" /> Cliquez pour uploader (JPG, PNG, WebP, GIF — 15MB max)</span>
          )}
        </button>
      )}
      {errMsg && (
        <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700" data-testid={`${testId}-error`}>{errMsg}</p>
      )}
    </div>
  );
}
